import { Injectable, computed, signal } from '@angular/core';
import { SipCredentialsDto } from 'src/app/shared/agent-desktop/telephony/models/sip-credentials.model';

/**
 * Holds the SIP credentials for the signed-in agent's current session.
 *
 * Deliberately dependency-free. `AgentStatusService` owns the session and writes what the server returned
 * with it; `CallFacadeService` reads it when it registers. Neither may inject the other — the status
 * service already depends on the facade — so a neutral store is what lets the value travel between them
 * without a cycle.
 *
 * Credentials are per session, so they must not outlive one: `clear()` is called wherever the agent's
 * session is torn down, otherwise the next user of this browser would register with the previous one's
 * secret.
 */
@Injectable({ providedIn: 'root' })
export class SipCredentialStore {
  private readonly credentials = signal<SipCredentialsDto | null>(null);

  /** The current credentials, or `null` when no session has provided any. */
  readonly value = this.credentials.asReadonly();

  /** True once credentials are available to register with. */
  readonly hasValue = computed(() => this.credentials() !== null);

  /**
   * Stores what the server returned. `username` is normalized the same way the credentials endpoint
   * normalizes it, so both sources hand the SIP stack an identical shape.
   */
  set(credentials: SipCredentialsDto): void {
    this.credentials.set({ ...credentials, username: credentials.username ?? '' });
  }

  /** Drops the credentials — called when the session ends, the user changes, or access is revoked. */
  clear(): void {
    this.credentials.set(null);
  }
}
